import { useState } from "react";
import { goToCheckout } from "@/components/CheckoutPage";
import { api, API } from "@/App";
import { BookOpen, Clapperboard, Eye, Film, Layers, Loader2, ShieldCheck, Smile, Wallet } from "lucide-react";
import { BinaryGrid } from "@/components/BinaryGrid";
import { DenoiseCompare } from "@/components/DenoiseCompare";
import { RecastVsSwap } from "@/components/RecastVsSwap";
import { DriverLibrary } from "@/components/DriverLibrary";

export const MINT = "#4ff2c2";
const MANUAL = "/downloads/KolorKlinic-Recast-Manual.pdf";

const COMPARES = [
  { id: "smile", title: "Keyframed smile", label: "smile", body: "A gentle smile eases in over a second and holds, steady from frame to frame. Every slider is a normal Resolve keyframe." },
  { id: "blink", title: "Remove blink here", label: "blink removed", body: "Playhead on the blink, one click. The open eyes from just before are tracked onto her face for the whole blink. No fal.ai cost." },
  { id: "addblink", title: "Add blink here", label: "blink added", body: "A staring take? One click keyframes a natural blink: a quick close and a slower reopen over about 6 frames." },
  { id: "handheld", title: "Handheld, tilted shot", label: "smile, face locked", body: "Real handheld drift and a dutch tilt. Recast locks the face still, adds a smile and a look into the lens, then puts it back on the moving shot without wobble." },
];

const FEATURES = [
  { icon: Smile, title: "10 sliders, or drag on the face", body: "Smile, eyelids, wink, brows, gaze, mouth open, head turn, nod and tilt, all keyframeable. Or drag a mouth corner, a pupil, a brow, the chin or the nose (turn, nod, tilt ring) right in the Resolve viewer, with the live value shown. Each drag sets a keyframe at the playhead; double-click a handle to reset it." },
  { icon: Eye, title: "Blinks, removed or added", body: "Unwanted blink in the hero take? Swap the open eyes back in, free. A staring take? Add a natural blink with one click." },
  { icon: Layers, title: "Your real pixels, not AI skin", body: "The AI only works out the new shape of the face. Recast then moves your own full-resolution pixels into that shape, so pores, lips, lashes and catchlights stay sharp, with no soft or doubled mouth." },
  { icon: Film, title: "Stable over time", body: "The face is locked still before the change and put back after, like a post stabilise-fix-unstabilise. Motion-compensated smoothing removes shimmer, and only the face area changes." },
  { icon: Wallet, title: "Your fal.ai account", body: "Pay once for the plugin. Processing runs on your own fal.ai key: no subscription, no markup, real cost history in the panel." },
  { icon: Clapperboard, title: "Linux, Intel & Apple Silicon, Windows", body: "Native OFX for DaVinci Resolve on all six builds, including Linux workstations and Intel Macs." },
];

const Compare = ({ d }) => (
  <div className="demo-card bg-[#0e1013] border border-[#2a2f3d]" data-testid={`recast-demo-${d.id}`}>
    <DenoiseCompare id={`recast-${d.id}`} before={`recast-${d.id}-before`} after={`recast-${d.id}-after`} color={MINT} beforeLabel="original take" afterLabel={d.label} aspect="aspect-[4/3]" start={12} v="5" />
    <div className="p-4">
      <h3 className="font-display uppercase text-base" style={{ color: MINT }}>{d.title}</h3>
      <p className="text-sm text-[#9ba3b4] leading-relaxed mt-1">{d.body}</p>
    </div>
  </div>
);

const Retarget = () => (
  <div className="demo-card bg-[#0e1013] border border-[#2a2f3d] grid grid-cols-1 lg:grid-cols-[2fr_1fr]" data-testid="recast-demo-retarget">
    <video src={`${API}/media/recast-retarget.mp4?v=5`} muted loop autoPlay playsInline className="w-full h-auto bg-black" data-testid="recast-demo-retarget-video" />
    <div className="p-5 flex flex-col justify-center">
      <h3 className="font-display uppercase text-base" style={{ color: MINT }}>Retarget from a driver clip</h3>
      <p className="text-sm text-[#9ba3b4] leading-relaxed mt-1">Copy expressions, gaze and mouth from a driver performance onto your shot. The actor's own head movement stays.</p>
    </div>
  </div>
);

export const RecastSection = ({ bins, dl, rebuild }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const buy = async () => {
    setBusy(true); setErr(null);
    try {
      goToCheckout({ lookup_key: "recast_lifetime" });
    } catch (e) { setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong, please try again."); setBusy(false); }
  };
  return (
    <section id="recast" className="scroll-mt-6 card p-6 space-y-6" data-testid="recast-section">
      <div className="flex flex-col md:flex-row md:items-end gap-5 justify-between">
        <div>
          <div className="chip mb-3" style={{ color: MINT, borderColor: MINT }}>new · €99 one-time · bring your own fal.ai key</div>
          <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">KolorKlinic <span style={{ color: MINT }}>Recast</span></h2>
          <p className="mt-2 font-display italic text-lg md:text-xl text-[#f2f4f8]" data-testid="recast-tagline">Fix the take without a reshoot.</p>
          <p className="mt-3 text-sm md:text-base text-[#9ba3b4] max-w-2xl leading-relaxed">
            Change a smile, remove a blink, move the eyes or nudge the head after the shoot, right inside DaVinci Resolve.
            Built on LivePortrait for the new face shape; Recast then moves your own full-res pixels into it, on a locked, stabilised face.
          </p>
        </div>
        <div className="flex flex-col gap-2 md:items-end">
          <button onClick={buy} disabled={busy} className="px-6 py-3 text-sm font-semibold uppercase text-black flex items-center justify-center gap-2 transition-opacity hover:opacity-85 disabled:opacity-60" style={{ background: MINT }} data-testid="recast-buy-btn">
            {busy && <Loader2 size={14} className="spin" />} Buy Recast · €99
          </button>
          <a href="#pricing" className="text-xs text-[#9ba3b4] underline" data-testid="recast-aipack-link">or get Gen Alpha + Recast for €169</a>
          <a href={MANUAL} download className="text-xs flex items-center gap-1.5 underline" style={{ color: MINT }} data-testid="recast-manual-link"><BookOpen size={13} /> User manual (PDF)</a>
          {err && <div className="text-xs text-[#ff453a]" data-testid="recast-buy-error">{err}</div>}
        </div>
      </div>
      <p className="text-xs text-[#8b93a7] -mb-2" data-testid="recast-compare-hint">Drag the sliders to compare the original take with Recast. Demo actor is AI-generated (no real person), processed with the Recast pipeline.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">{COMPARES.map((d) => <Compare key={d.id} d={d} />)}</div>
      <Retarget />
      <DriverLibrary color={MINT} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`recast-feature-${title.split(" ")[0].toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: MINT }} /><h3 className="font-display uppercase text-base">{title}</h3></div>
            <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
      <RecastVsSwap />
      <div className="flex gap-3 items-start border border-[#2a2f3d] bg-[#0e1013] p-4 text-sm text-[#9ba3b4]" data-testid="recast-consent-note">
        <ShieldCheck size={18} className="flex-none mt-0.5" style={{ color: MINT }} />
        <span>Use Recast only on performers who agreed to it. The plugin asks you to confirm consent before every job, and realistic edits of real people may need to be disclosed where you publish them.</span>
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h3 className="font-display uppercase text-xl">Downloads</h3>
          <a href={MANUAL} download className="ml-auto px-4 py-2 text-xs font-semibold uppercase border flex items-center gap-2 transition-colors hover:bg-[#4ff2c2] hover:text-black" style={{ borderColor: MINT, color: MINT }} data-testid="recast-manual-download-btn"><BookOpen size={14} /> Recast manual · PDF</a>
        </div>
        <BinaryGrid prefix="recast-" bins={bins} dl={dl} rebuild={rebuild} />
      </div>
    </section>
  );
};
